import { NavLink, Outlet } from 'react-router-dom';
import { useArtifact } from '../context/ArtifactContext.js';

// ─── Icons ────────────────────────────────────────────────────────

function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        fill={active ? 'currentColor' : 'none'}
        fillOpacity={active ? 0.15 : 0}
      />
      <path d="M9 21V12h6v9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function CategoryIcon({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="2"
        fill={active ? 'currentColor' : 'none'} fillOpacity={active ? 0.15 : 0} />
      <rect x="13" y="3" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="2"
        fill={active ? 'currentColor' : 'none'} fillOpacity={active ? 0.15 : 0} />
      <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="2"
        fill={active ? 'currentColor' : 'none'} fillOpacity={active ? 0.15 : 0} />
      <rect x="13" y="13" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="2"
        fill={active ? 'currentColor' : 'none'} fillOpacity={active ? 0.15 : 0} />
    </svg>
  );
}

function TxnIcon({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6h16M4 10h16M4 14h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path
        d="M16 16l3 3 3-3M19 19V14"
        stroke={active ? 'currentColor' : 'none'}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
      <circle cx="19" cy="17" r="0" fill={active ? 'currentColor' : 'none'} />
    </svg>
  );
}

function LoanIcon({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 12h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path
        d="M3 12C3 7.03 7.03 3 12 3s9 4.03 9 9"
        stroke="currentColor" strokeWidth="2"
        fill={active ? 'currentColor' : 'none'} fillOpacity={active ? 0.15 : 0}
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"
        fill={active ? 'currentColor' : 'none'} fillOpacity={active ? 0.3 : 0} />
    </svg>
  );
}

function TrendIcon({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <polyline
        points="3,17 8,12 12,14 16,9 21,5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={active ? 'currentColor' : 'none'}
        fillOpacity={active ? 0.08 : 0}
      />
      <path d="M3 21h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function SettingsIcon({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"
        fill={active ? 'currentColor' : 'none'} fillOpacity={active ? 0.25 : 0} />
      <path
        d="M12 2v2m0 16v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M2 12h2m16 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Nav items ───────────────────────────────────────────────────

const NAV_ITEMS = [
  { to: '/',             label: 'Dashboard',    Icon: HomeIcon,     end: true  },
  { to: '/categories',   label: 'Categories',   Icon: CategoryIcon, end: false },
  { to: '/transactions', label: 'Transactions', Icon: TxnIcon,      end: false },
  { to: '/loans',        label: 'Loans',        Icon: LoanIcon,     end: false },
  { to: '/trends',       label: 'Trends',       Icon: TrendIcon,    end: false },
  { to: '/settings',     label: 'Settings',     Icon: SettingsIcon, end: false },
] as const;

// ─── App Shell ───────────────────────────────────────────────────

export function AppShell() {
  const { lock } = useArtifact();

  return (
    <div className="flex flex-col min-h-screen bg-[var(--color-bg)]">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[var(--color-bg)] border-b border-[var(--color-border)]">
        <span
          className="text-[var(--color-accent)] font-bold text-lg tracking-tight"
          aria-label="PersonalFinance"
        >
          PF
        </span>
        <button
          onClick={lock}
          aria-label="Lock — clear decrypted data from memory"
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-raised)] transition-colors duration-[var(--duration-fast)]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 11V7a4 4 0 018 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </header>

      {/* Main + Side rail on tablet+ */}
      <div className="flex flex-1 min-h-0">
        {/* Side rail — hidden on mobile */}
        <nav
          aria-label="Main navigation"
          className="hidden md:flex flex-col w-[72px] lg:w-56 flex-shrink-0 border-r border-[var(--color-border)] py-4 gap-1 sticky top-[57px] h-[calc(100vh-57px)] overflow-y-auto"
        >
          {NAV_ITEMS.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              aria-label={label}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 px-4 py-3 rounded-lg mx-2 min-h-[44px]',
                  'text-sm font-medium transition-colors duration-[var(--duration-fast)]',
                  isActive
                    ? 'text-[var(--color-accent)] bg-[var(--color-accent-subtle)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-raised)]',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <Icon active={isActive} />
                  <span className="hidden lg:block">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Screen content */}
        <main
          id="main-content"
          className="flex-1 min-w-0 overflow-y-auto"
        >
          {/* Bottom tab bar padding on mobile */}
          <div className="pb-[72px] md:pb-0">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom tab bar — mobile only */}
      <nav
        aria-label="Main navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around bg-[var(--color-surface)] border-t border-[var(--color-border)] h-[72px] px-1 safe-area-inset-bottom"
      >
        {NAV_ITEMS.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            aria-label={label}
            className={({ isActive }) =>
              [
                'flex flex-col items-center justify-center gap-0.5 min-w-[44px] min-h-[44px] px-2 rounded-xl',
                'transition-colors duration-[var(--duration-fast)]',
                isActive
                  ? 'text-[var(--color-accent)]'
                  : 'text-[var(--color-text-secondary)]',
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <>
                <Icon active={isActive} />
                <span className="text-[10px] font-medium">{label}</span>
                {/* Active indicator dot */}
                <span
                  aria-hidden="true"
                  className={[
                    'w-1 h-1 rounded-full mt-0.5 transition-opacity duration-[var(--duration-fast)]',
                    isActive
                      ? 'bg-[var(--color-accent)] opacity-100'
                      : 'opacity-0',
                  ].join(' ')}
                />
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
